import { ChevronDown, ChevronUp, FileText } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import type { LibraryInquirySummary } from '../../model/types';

import { SummaryThumbs } from '@/components/SummaryThumbs';
import {
  SUPPORT_REASON_BECOME_LIBRARY_ADVISOR,
  SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK,
  SupportForm,
} from '@/components/supportForm';
import { Badge, BadgeText } from '@/components/ui/badge';
import { BulletDot } from '@/components/ui/bullet-dot';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useSupportPrompt } from '@/features/support-contact';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useInquirySummaryRatingSurvey } from '@/features/surveys';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  type InquirySummarySource,
  trackInquirySummaryFollowUpOpened,
  trackInquirySummaryFollowUpSubmitted,
  trackInquirySummaryRated,
  trackInquirySummaryToggled,
  trackInquirySummaryViewed,
} from '@/util/analyticsEvents';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { shouldFireImpression } from '@/util/impressionDedup';

/**
 * The card's data, aliased from the generated schema rather than restated.
 *
 * Every string is composed on the server from a grid of expert positions, not
 * here. The app cannot import that package yet, and a second copy of the rules
 * about what an expert said is how a misattribution gets in.
 */
export type InquiryGroupSummary = LibraryInquirySummary;

type Bullet = InquiryGroupSummary['bullets'][number];

/**
 * The marker is uniform, so the agreement a reader cannot see is named for a
 * screen reader. A split is the one thing a reader scans for.
 */
const AGREEMENT_LABEL: Partial<Record<Bullet['agreement'], string>> = {
  split: 'Specialists differ on this topic',
  single_voice: 'One specialist addressed this topic',
};

/** The bold words that open the question or a bullet, set off by two spaces. */
function LeadIn({ children }: { children: string }) {
  return (
    <Text className="font-body font-semibold text-base leading-relaxed text-typography-900">
      {`${children}  `}
    </Text>
  );
}

/**
 * The card's one badge. Beta rides inside it rather than in a second chip, which
 * crowded the header row. Beta stays through the wider rollout, when most readers see
 * a generated summary for the first time, and comes off on its own schedule rather
 * than with the feature flag: drop the suffix here once the votes look healthy.
 */
const SUMMARY_BADGE_LABEL = 'Inquiry summary · Beta';

/**
 * The fold control, at the foot of the card like the profile's Show more.
 *
 * Reads Show more and Show less, matching the profile; a topic count was tried and
 * dropped. It is `secondary-700` and semibold: `secondary-600` clears contrast but this
 * slate ramp reads as gray at that stop, and the brand green that clears it is closer
 * still to the body text. The spoken label starts with the visible words.
 */
function SummaryToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const visibleLabel = expanded ? 'Show less' : 'Show more';

  return (
    <Pressable
      onPress={onToggle}
      hitSlop={getAccessibleHitSlop(20)}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      accessibilityLabel={`${visibleLabel} in the inquiry summary`}
      className="self-start web:cursor-pointer">
      <HStack className="items-center gap-1">
        <Text size="sm" className="font-semibold text-secondary-700">
          {visibleLabel}
        </Text>
        <Icon as={expanded ? ChevronUp : ChevronDown} size="sm" className="text-secondary-700" />
      </HStack>
    </Pressable>
  );
}

/**
 * Names the system and no source. Plain "Summarized by Primum" would read as a human
 * summary, since staff write Library posts too.
 *
 * Shown on the collapsed card as well, because the conclusion there is generated. It
 * stays in the footer beside the thumbs: moving it to the top was suggested and not
 * strongly held, and a note about how a summary was made reads as a footnote.
 */
function ProvenanceNote({ footerText }: { footerText: string }) {
  return (
    <Text className={twJoin('shrink text-typography-700', footerText)}>
      Summarized by Primum AI
    </Text>
  );
}

/**
 * What the card says after a vote, one per verdict.
 *
 * A reader who liked the summary is asked to help as an advisor, and one who did not is
 * asked what to fix. Both open the in-app support form with the matching reason chosen,
 * the same way the Fellows Corner mentor card does, so there is no shared address to
 * publish and no mail app to depend on.
 *
 * Each line fits one row on a small phone. The longer first drafts wrapped and left a
 * single word on the second row.
 */
const FOLLOW_UP = {
  up: {
    lead: 'Thanks! ',
    link: 'Become an Inquiry Library advisor',
    kind: 'advisor',
    reason: SUPPORT_REASON_BECOME_LIBRARY_ADVISOR,
    message: () => "I'd like to be an advisor on the Inquiry Library.",
  },
  down: {
    lead: 'Thanks! ',
    link: 'What could we do better?',
    kind: 'feedback',
    reason: SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK,
    message: (postId: number) => `About the summary on inquiry ${postId}: `,
  },
} as const;

/**
 * The line under the footer once a reader has voted, gone again if the vote is taken
 * back. It shows only to readers who engaged, so the rest of the cards stay quiet.
 */
function VoteFollowUp({
  verdict,
  postId,
  source,
  footerText,
}: {
  verdict: 'up' | 'down';
  postId: number;
  source: InquirySummarySource;
  footerText: string;
}) {
  const { currentUser } = useAuthStatus();

  const followUp = FOLLOW_UP[verdict];

  const supportPrompt = useSupportPrompt({
    source: 'inquiry_group_summary',
    draft: followUp.message(postId),
  });

  return (
    <>
      <Text className={twJoin('text-typography-800', footerText)}>
        {followUp.lead}
        <Text
          accessibilityRole="button"
          onPress={() => {
            trackInquirySummaryFollowUpOpened({ post_id: postId, kind: followUp.kind, source });

            supportPrompt.open();
          }}
          className={twJoin(
            'font-medium text-typography-900 underline web:cursor-pointer',
            footerText
          )}>
          {followUp.link}
        </Text>
      </Text>

      {supportPrompt.isFormOpen ? (
        <SupportForm
          isOpen={supportPrompt.isFormOpen}
          onClose={supportPrompt.closeForm}
          onSubmitted={() =>
            trackInquirySummaryFollowUpSubmitted({ post_id: postId, kind: followUp.kind, source })
          }
          npi={currentUser?.npiNumber ?? ''}
          institution={currentUser?.institution ?? ''}
          state={currentUser?.state ?? ''}
          firstName={currentUser?.firstName ?? ''}
          lastName={currentUser?.lastName ?? ''}
          identifiesAsDoctor={currentUser?.identifiesAsDoctor}
          initialValues={{
            reason: followUp.reason,
            name: currentUser ? getProfileNameAndInitials(currentUser).name : '',
            email: currentUser?.email ?? '',
            message: followUp.message(postId),
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The case box on every surface: a 1px border in the badge's own blue, `secondary-600`
 * (`secondary-300` in dark, the same stop the badge swaps to), on a `background-0` fill. It is
 * the card's one accent, so the case is easy to find, and a brand step, so it follows the brand
 * refresh with the badge.
 */
const CASE_BOX_CLASS_NAME =
  'items-start gap-2 rounded-md border border-secondary-600 bg-background-0 p-3 dark:border-secondary-300';

/**
 * The card's border, per variant. The default keeps `outline-500`, as every flag-off post has
 * today. On a post that shows its answers it is `outline-200`, the one line color of the answers
 * view, so the summary, the question and the answer cards share one border and one gray fill.
 */
const CARD_BORDER_CLASS_NAME = {
  default: 'border-outline-500',
  inquiryLibraryAnswers: 'border-outline-200',
} as const;

/**
 * The inquiry summary on an Inquiry Library post.
 *
 * **It reads as part of the post.** The type is the body's own. The gray
 * ground, border, badge and case box are the only difference.
 *
 * **The ground is `background-50`,** a true neutral. `background-muted` has a
 * green cast that clashes with the blue case box.
 *
 * **It opens expanded on a wide layout and collapsed on a compact one,** where
 * the screen is scarce and the post should stay near the fold. Collapsed, it
 * shows the question, conclusion and case line with the bullets behind the
 * toggle. The reader's own toggle wins from then on, even if the window is
 * resized.
 *
 * **In Research it always reads as compact.** A Research result opens in a
 * phone-width column on every layout: the desktop detail pane is a fixed
 * 400px beside the conversation. Breakpoint classes there answer for the
 * window rather than the column, which opened every bullet in a narrow pane
 * and set the footer at the 12px that read as too small on a phone, so
 * `source` picks the compact default and type instead.
 *
 * **On a post that shows its answers it is a variant.** `variant="inquiryLibraryAnswers"` keeps the bold
 * restated question and opens collapsed on every layout, as a quick tip at the top of the page: the
 * question, the conclusion and the case line, with the bullets behind Show more. The reader's
 * toggle and its analytics work as they do everywhere else. Every other surface keeps the default.
 *
 * **The list is what truncates, never the sentence.** Clipped at a line count,
 * "would rule it out, if MRD negativity is confirmed" reads "would rule it
 * out", a different clinical position rather than a shorter one. Whole bullets
 * go behind the toggle and each keeps its qualifier.
 */
export function InquiryGroupSummaryCard({
  summary,
  postId,
  source = 'community',
  variant = 'default',
}: {
  summary: InquiryGroupSummary;
  postId: number;
  source?: InquirySummarySource;
  /** `inquiryLibraryAnswers` is the card on a post that shows its answers. See the component notes. */
  variant?: 'default' | 'inquiryLibraryAnswers';
}) {
  const isInquiryLibraryAnswers = variant === 'inquiryLibraryAnswers';

  const { expertCount } = summary;

  const topicCount = summary.bullets.length;

  /** The endpoint returns no bullets when the specialists cannot be numbered. */
  const expandable = topicCount > 0;

  const isCompactLayout = useIsCompactLayout();

  const isCompact = isCompactLayout || source === 'library_chat';

  const footerText = isCompact ? 'text-sm' : 'text-sm md:text-xs';

  /** Null until the reader toggles, so the default keeps following the layout. */
  const [chosen, setChosen] = useState<boolean | null>(null);

  const expanded = expandable && (chosen ?? (!isInquiryLibraryAnswers && !isCompact));

  /**
   * Once per post per surface per session: the denominator for every other
   * summary event, so a remount or a return to the post must not count again.
   * Keyed by surface too, so each surface's events have views to divide by.
   */
  useEffect(() => {
    if (shouldFireImpression(`inquiry-summary:${source}:${postId}`)) {
      trackInquirySummaryViewed({
        post_id: postId,
        expert_count: expertCount,
        topic_count: topicCount,
        source,
      });
    }
  }, [postId, expertCount, topicCount, source]);

  const toggle = () => {
    const next = !expanded;

    setChosen(next);

    trackInquirySummaryToggled({ post_id: postId, expanded: next, source });
  };

  return (
    <VStack
      className={twJoin(
        'w-full gap-3 rounded-md border',
        CARD_BORDER_CLASS_NAME[variant],
        'bg-background-50 p-3',
        !isCompact && 'md:p-4'
      )}>
      <VStack className="gap-2">
        <HStack className="flex-wrap items-center gap-x-2 gap-y-1">
          {/* The stop moves with the theme: white on secondary-600 fails AA
                  in dark, and secondary-300 is the same colour there. */}
          <Badge size="sm" className="bg-secondary-600 dark:bg-secondary-300">
            <BadgeText className="text-typography-white">{SUMMARY_BADGE_LABEL}</BadgeText>
          </Badge>

          <Text size="xs" className="text-typography-700">
            {expertCount === 1 ? '1 Primum Specialist' : `${expertCount} Primum Specialists`}
          </Text>
        </HStack>

        {/* The conclusion is pinned to regular so it never inherits the
              question's bold. max-w-5xl sits on each block, not the header, so
              the toggle stays at the right edge. */}
        <Text className="max-w-5xl font-body text-base leading-relaxed text-typography-800">
          {summary.question ? <LeadIn>{summary.question}</LeadIn> : null}
          <Text className="font-body font-normal text-base leading-relaxed text-typography-800">
            {summary.conclusion}
          </Text>
        </Text>

        {/* The submitter's facts only, never an expert's. The outline is the card's one
              accent, so the case is easy to find, see CASE_BOX_CLASS_NAME. */}
        {summary.caseContext ? (
          <HStack className={CASE_BOX_CLASS_NAME}>
            <Icon as={FileText} size="sm" className="mt-0.5 shrink-0 text-secondary-800" />
            <Text
              size="sm"
              className="max-w-5xl flex-1 font-body leading-relaxed text-typography-800">
              <Text className="font-body font-semibold text-sm leading-relaxed text-secondary-800">
                {'Case: '}
              </Text>
              {summary.caseContext}
            </Text>
          </HStack>
        ) : null}
      </VStack>

      {expanded ? (
        <VStack className="max-w-5xl gap-2.5">
          {/* Keyed by position too, since two bullets can share a heading. */}
          {summary.bullets.map((bullet, index) => (
            <HStack key={`${index}-${bullet.topic}`} className="items-start gap-3">
              <BulletDot accessibilityLabel={AGREEMENT_LABEL[bullet.agreement]} />

              {/* A bullet with no heading shows its text alone. */}
              <Text className="flex-1 font-body text-base leading-relaxed text-typography-800">
                {bullet.topic.trim() ? <LeadIn>{bullet.topic}</LeadIn> : null}
                {bullet.detail}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : null}

      {expandable ? <SummaryToggle expanded={expanded} onToggle={toggle} /> : null}

      <SummaryFeedback
        postId={postId}
        expanded={expanded}
        source={source}
        footerText={footerText}
      />
    </VStack>
  );
}

const THUMB_LABELS = {
  up: 'This summary was helpful',
  down: 'This summary was not helpful',
} as const;

/**
 * The card's footer: the provenance note and the only per-summary feedback signal,
 * on one line, then a follow-up line once the reader votes. The thumbs judge the content
 * just above, so they stay next to it, and the follow-up comes last.
 *
 * Shown on the collapsed card too. The vote is three-state rather than boolean:
 * a reader who mis-taps needs a way back, and an unanswered card is not one
 * somebody disliked. The selected thumb sits on a tinted chip, because a shade of
 * color alone read as unselected on a phone. The footer's type is a step larger below
 * the desktop breakpoint, since 12px read as too small on a phone.
 *
 * **One negative control, not two.** A report control for a misattributed claim
 * sat beside the thumbs and went: with two negative controls an unsure reader
 * picks the softer one, and the incident arrives filed as a preference.
 *
 * It asks about helpfulness rather than accuracy, the narrower and honest
 * claim: judging accuracy means reading every reply against the card.
 */
function SummaryFeedback({
  postId,
  expanded,
  source,
  footerText,
}: {
  postId: number;
  expanded: boolean;
  source: InquirySummarySource;
  footerText: string;
}) {
  const [verdict, setVerdict] = useState<'up' | 'down' | null>(null);

  const { currentUser } = useAuthStatus();

  const submitVote = useInquirySummaryRatingSurvey({ postId, enabled: Boolean(currentUser) });

  /** Pressing the selected thumb again takes the vote back, reported as `cleared`. */
  const rate = (pressed: 'up' | 'down') => {
    const next = verdict === pressed ? null : pressed;

    setVerdict(next);

    trackInquirySummaryRated({ post_id: postId, rating: next ?? 'cleared', expanded, source });

    if (next) {
      submitVote(next).catch(() => undefined);
    }
  };

  return (
    <VStack className="gap-2 pt-1">
      <HStack className="items-center justify-between gap-3">
        <ProvenanceNote footerText={footerText} />

        <SummaryThumbs
          vote={verdict}
          onRate={rate}
          labels={THUMB_LABELS}
          textClassName={footerText}
        />
      </HStack>

      {verdict ? (
        <VoteFollowUp
          key={verdict}
          verdict={verdict}
          postId={postId}
          source={source}
          footerText={footerText}
        />
      ) : null}
    </VStack>
  );
}
